import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Coachmark.css';
const TOOLTIP_OFFSET = 12;
const VIEWPORT_MARGIN = 12;
function computePosition(target, placement, tooltipSize) {
    const rect = target.getBoundingClientRect();
    const halo = {
        top: rect.top - 4,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
    };
    let top = rect.top;
    let left = rect.left;
    switch (placement) {
        case 'top':
            top = rect.top - tooltipSize.height - TOOLTIP_OFFSET;
            left = rect.left + rect.width / 2 - tooltipSize.width / 2;
            break;
        case 'bottom':
            top = rect.bottom + TOOLTIP_OFFSET;
            left = rect.left + rect.width / 2 - tooltipSize.width / 2;
            break;
        case 'left':
            top = rect.top + rect.height / 2 - tooltipSize.height / 2;
            left = rect.left - tooltipSize.width - TOOLTIP_OFFSET;
            break;
        case 'right':
        default:
            top = rect.top + rect.height / 2 - tooltipSize.height / 2;
            left = rect.right + TOOLTIP_OFFSET;
            break;
    }
    if (left + tooltipSize.width > window.innerWidth - VIEWPORT_MARGIN) {
        left = window.innerWidth - tooltipSize.width - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN)
        left = VIEWPORT_MARGIN;
    if (top + tooltipSize.height > window.innerHeight - VIEWPORT_MARGIN) {
        top = window.innerHeight - tooltipSize.height - VIEWPORT_MARGIN;
    }
    if (top < VIEWPORT_MARGIN)
        top = VIEWPORT_MARGIN;
    return { top, left, halo };
}
function SingleCoachmark({ item, onDismiss, showCloseButton }) {
    const [rect, setRect] = useState(null);
    const tooltipRef = useRef(null);
    const { target } = item;
    const placement = item.placement ?? 'bottom';
    const measure = useCallback(() => {
        if (!target || !tooltipRef.current)
            return;
        const tooltipSize = {
            width: tooltipRef.current.offsetWidth || 260,
            height: tooltipRef.current.offsetHeight || 80,
        };
        setRect(computePosition(target, placement, tooltipSize));
    }, [target, placement]);
    useLayoutEffect(() => {
        measure();
    }, [measure]);
    useEffect(() => {
        if (!target)
            return undefined;
        const onScrollOrResize = () => measure();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(onScrollOrResize);
            observer.observe(target);
        }
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
            observer?.disconnect();
        };
    }, [target, measure]);
    if (!target)
        return null;
    return (_jsxs(_Fragment, { children: [rect && (_jsx("div", { className: "coachmark-halo", style: {
                    position: 'fixed',
                    top: rect.halo.top,
                    left: rect.halo.left,
                    width: rect.halo.width,
                    height: rect.halo.height,
                    pointerEvents: 'none',
                    zIndex: 1099,
                } })), _jsxs("div", { ref: tooltipRef, className: `coachmark-tooltip coachmark-tooltip--${placement}${!showCloseButton ? ' coachmark-tooltip--no-dismiss' : ''}`, style: {
                    position: 'fixed',
                    top: rect?.top ?? -9999,
                    left: rect?.left ?? -9999,
                    visibility: rect ? 'visible' : 'hidden',
                    zIndex: 1100,
                }, role: "tooltip", children: [item.title && _jsx("div", { className: "coachmark-title", children: item.title }), _jsx("div", { className: "coachmark-description", children: item.description }), showCloseButton ? (_jsx("button", { type: "button", className: "coachmark-dismiss", "aria-label": "Dismiss coachmark", onClick: onDismiss, children: _jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })) : null] })] }));
}
export function CoachmarkOverlay({ items, onDismiss, scrimDismissable = true, escapeDismissable = true, showTooltipCloseButton = true, }) {
    useEffect(() => {
        if (items.length === 0 || !escapeDismissable)
            return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onDismiss();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [items.length, onDismiss, escapeDismissable]);
    if (items.length === 0)
        return null;
    const container = typeof document !== 'undefined' ? document.body : null;
    if (!container)
        return null;
    return createPortal(_jsxs(_Fragment, { children: [_jsx("div", { className: "coachmark-scrim", style: {
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(15, 23, 42, 0.35)',
                    zIndex: 1098,
                }, onClick: scrimDismissable ? onDismiss : undefined, role: "presentation", "aria-hidden": true }), items.map((item) => (_jsx(SingleCoachmark, { item: item, onDismiss: onDismiss, showCloseButton: showTooltipCloseButton }, item.key)))] }), container);
}
